import React, { useState } from 'react';
import { TextField, Typography, Button, Box, Paper, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { ClientConfig, PromptConfig } from '../../services/module4Service';
import TabPanel from './TabPanel';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TabPanelPromptProps {
  config: ClientConfig;
  activeTab: number;
  handlePromptChange: (field: string, value: any) => void;
}

// GitHub-flavored markdown renderer 
const MarkdownRenderer: React.FC<{ content: string }> = ({ content }) => {
  return (
    <Box sx={{ padding: '16px', lineHeight: '1.6' }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom styling for headers
          h1: ({ children }) => (
            <Typography variant="h4" component="h1" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 2, mt: 3 }}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="h5" component="h2" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 1.5, mt: 2.5 }}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="h6" component="h3" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 1, mt: 2 }}>
              {children}
            </Typography>
          ),
          // Code blocks with better styling
          code: ({ children, className }) => {
            const isInline = !className;
            return isInline ? (
              <Box 
                component="code" 
                sx={{ 
                  backgroundColor: '#f6f8fa',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  fontSize: '85%',
                  fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
                  color: '#24292f'
                }}
              >
                {children}
              </Box>
            ) : (
              <Box 
                component="pre"
                sx={{
                  backgroundColor: '#f6f8fa',
                  border: '1px solid #d1d9e0',
                  borderRadius: '6px',
                  padding: '16px',
                  overflow: 'auto',
                  fontSize: '85%',
                  lineHeight: '1.45',
                  fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
                  margin: '16px 0'
                }}
              >
                <code>{children}</code>
              </Box>
            );
          },
          // Lists with better styling
          ul: ({ children }) => (
            <Box component="ul" sx={{ paddingLeft: '20px', margin: '8px 0' }}>
              {children}
            </Box>
          ),
          ol: ({ children }) => (
            <Box component="ol" sx={{ paddingLeft: '20px', margin: '8px 0' }}>
              {children}
            </Box>
          ),
          li: ({ children }) => (
            <Box component="li" sx={{ margin: '4px 0' }}>
              {children}
            </Box>
          ),
          // Tables with Material-UI styling
          table: ({ children }) => (
            <Box sx={{ overflow: 'auto', margin: '16px 0' }}>
              <Box 
                component="table" 
                sx={{ 
                  borderCollapse: 'collapse',
                  width: '100%',
                  border: '1px solid #d1d9e0'
                }}
              >
                {children}
              </Box>
            </Box>
          ),
          th: ({ children }) => (
            <Box 
              component="th" 
              sx={{ 
                padding: '8px 12px',
                borderBottom: '1px solid #d1d9e0',
                backgroundColor: '#f6f8fa',
                fontWeight: 'bold',
                textAlign: 'left'
              }}
            >
              {children}
            </Box>
          ),
          td: ({ children }) => (
            <Box 
              component="td" 
              sx={{ 
                padding: '8px 12px',
                borderBottom: '1px solid #d1d9e0'
              }}
            >
              {children}
            </Box>
          ),
          // Blockquotes
          blockquote: ({ children }) => (
            <Box 
              sx={{
                borderLeft: '4px solid #d1d9e0',
                paddingLeft: '16px',
                margin: '16px 0',
                color: '#656d76',
                fontStyle: 'italic'
              }}
            >
              {children}
            </Box>
          ),
          // Strong and emphasis
          strong: ({ children }) => (
            <Box component="strong" sx={{ fontWeight: 'bold' }}>
              {children}
            </Box>
          ),
          em: ({ children }) => (
            <Box component="em" sx={{ fontStyle: 'italic' }}>
              {children}
            </Box>
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

const TabPanelPrompt: React.FC<TabPanelPromptProps> = ({
  config,
  activeTab,
  handlePromptChange
}) => {
  const [promptViewTab, setPromptViewTab] = useState(0);

  return (
    <TabPanel value={activeTab} index={2}>
      <Typography variant="h6" gutterBottom>System Prompt</Typography>
      
      {/* Prompt View Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={promptViewTab} onChange={(e, newValue) => setPromptViewTab(newValue)}>
          <Tab label="Edit" />
          <Tab label="Preview" />
        </Tabs>
      </Box>

      {promptViewTab === 0 ? (
        <TextField
          fullWidth
          label="System Prompt"
          value={config.prompt.system_prompt}
          onChange={(e) => handlePromptChange('system_prompt', e.target.value)}
          required
          multiline
          rows={35}
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '14px',
              lineHeight: '1.5'
            }
          }}
          helperText="Supports markdown formatting. Use the Preview tab to see how it will render."
        />
      ) : (
        <Paper 
          variant="outlined" 
          sx={{ 
            minHeight: '800px',
            maxHeight: '800px', 
            overflow: 'auto',
            backgroundColor: '#ffffff',
            border: '1px solid #d1d9e0'
          }}
        >
          <MarkdownRenderer content={config.prompt.system_prompt} />
        </Paper>
      )}
      
      {/* Collapsible Output Configuration Section */}
      <Accordion defaultExpanded={false} sx={{ mt: 3 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="output-config-content"
          id="output-config-header"
        >
          <Typography variant="h6">Output Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Output Format"
              value={config.prompt.output_format}
              onChange={(e) => handlePromptChange('output_format', e.target.value)}
              required
            />
            
            <TextField
              fullWidth
              type="number"
              label="Temperature"
              value={config.prompt.temperature}
              onChange={(e) => handlePromptChange('temperature', parseFloat(e.target.value))}
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              required
              helperText="Controls randomness: 0 = deterministic, 1 = very random"
            />
            
            <TextField
              fullWidth
              type="number"
              label="Max Tokens"
              value={config.prompt.max_tokens}
              onChange={(e) => handlePromptChange('max_tokens', parseInt(e.target.value))}
              inputProps={{ min: 1, max: 4000 }}
              required
              helperText="Maximum number of tokens in the response"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </TabPanel>
  );
};

export default TabPanelPrompt;
